import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Youtube, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import YouTubeHero, { extractYouTubeId } from "../../components/YouTubeHero";

export default function AdminSettings() {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setUrl(data?.home_video_url || "");
      setTitle(data?.home_video_title || "");
    } catch { /* ignore */ }
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (url && !extractYouTubeId(url)) {
      toast.error("That doesn't look like a YouTube URL.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        home_video_url: url || null,
        home_video_title: title || null,
      });
      toast.success("Home page video updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the YouTube video from the home page?")) return;
    setSaving(true);
    try {
      await api.put("/admin/settings", { home_video_url: null, home_video_title: null });
      setUrl("");
      setTitle("");
      toast.success("Video removed");
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const previewId = extractYouTubeId(url);

  return (
    <div>
      <h1 className="font-display font-black text-4xl tracking-tight">Site settings</h1>
      <p className="mt-2 text-sm text-neutral-600">Control content that appears across the storefront.</p>

      <div className="mt-8 border border-neutral-200 p-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 bg-[#FF0000] text-white grid place-items-center">
            <Youtube className="w-5 h-5" />
          </div>
          <h2 className="font-display font-bold text-xl">Home page YouTube video</h2>
        </div>
        <p className="text-sm text-neutral-600">
          Paste any YouTube URL (watch, youtu.be, shorts or embed). The video appears at the very top of the home page on every visit. Leave the URL blank and save to hide it.
        </p>

        <div className="mt-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="video-url">YouTube URL</Label>
            <Input
              id="video-url"
              data-testid="video-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              className="rounded-none border-black h-11"
            />
            {url && (
              previewId
                ? <p className="text-xs text-[#00A355] font-semibold">✓ Valid YouTube ID: {previewId}</p>
                : <p className="text-xs text-[#FF2A00] font-semibold">Not a recognised YouTube URL.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="video-title">Caption (optional)</Label>
            <Input
              id="video-title"
              data-testid="video-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New arrivals — Spring drop"
              className="rounded-none border-black h-11"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            data-testid="save-video-btn"
            onClick={save}
            disabled={saving || !loaded}
            className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-11 px-5 font-semibold"
          >
            {saving ? "Saving…" : "Save & publish"}
          </Button>
          {url && (
            <Button
              data-testid="remove-video-btn"
              onClick={remove}
              variant="outline"
              disabled={saving}
              className="rounded-none border-black h-11 px-5"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Remove
            </Button>
          )}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-none border border-black h-11 px-5 inline-flex items-center gap-2 text-sm font-semibold hover:bg-black hover:text-white"
          >
            <ExternalLink className="w-4 h-4" /> Open home page
          </a>
        </div>

        {previewId && (
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">Preview</div>
            <div className="border border-neutral-200">
              <YouTubeHero url={url} title={title} />
            </div>
          </div>
        )}
      </div>

      <WhatsAppSettingsLink />
    </div>
  );
}


function WhatsAppSettingsLink() {
  return (
    <div className="mt-10 border border-black p-6 bg-[#25D366]/5 max-w-3xl">
      <h2 className="font-display font-black text-2xl mb-1">WhatsApp settings</h2>
      <p className="text-sm text-neutral-700 mb-3">
        WhatsApp configuration and test sender moved to its own page for easier access.
      </p>
      <a
        data-testid="whatsapp-settings-link"
        href="/admin/whatsapp"
        className="inline-flex items-center h-11 px-5 border border-black bg-[#25D366] text-white font-bold hover:bg-[#1eb558]"
      >
        Open WhatsApp settings →
      </a>
    </div>
  );
}
